import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Units = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📏 CSS Units</h1>

      <p className="tutorial-intro">
        CSS uses <b>units</b> to define sizes, spacing, and positioning of
        elements. Units can be <b>absolute</b> (fixed values like{" "}
        <code>px</code>) or <b>relative</b> (scalable values like{" "}
        <code>em</code>, <code>rem</code>,<code>%</code>, <code>vh</code>, and{" "}
        <code>vw</code>).
      </p>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: Absolute Units</h2>
        <p>
          Absolute units are fixed and do not scale with the screen size. Common
          ones include <code>px</code> (pixels), <code>cm</code> (centimeters),{" "}
          <code>mm</code> (millimeters), and <code>pt</code> (points).
        </p>

        <CodeCopyBlock
          code={`h1 {
  font-size: 24px;   /* fixed size */
}
p {
  font-size: 12pt;   /* fixed point size */
}`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <h1 style={{ fontSize: "24px", background: "#fef9c3" }}>
            Heading with 24px
          </h1>
          <p style={{ fontSize: "12pt", background: "#fde68a" }}>
            Paragraph with 12pt font-size
          </p>
        </div>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">
          👨‍💻 Example 2: Relative Units (em, rem)
        </h2>
        <p>
          Relative units scale depending on other values. <code>em</code> is
          relative to the parent’s font size, while <code>rem</code> is relative
          to the root (HTML) font size.
        </p>

        <CodeCopyBlock
          code={`html {
  font-size: 16px; /* root font size */
}
div {
  font-size: 1.5em;  /* 1.5 × parent font-size */
}
p {
  font-size: 2rem;   /* 2 × root font-size */
}`}
          language="css"
        />

        <div
          style={{
            marginTop: "1rem",
            border: "1px solid #ddd",
            padding: "10px",
          }}
        >
          <div style={{ fontSize: "16px" }}>
            Parent (16px)
            <div style={{ fontSize: "1.5em", background: "#bfdbfe" }}>
              Child with 1.5em (24px)
              <p style={{ fontSize: "2rem", background: "#93c5fd" }}>
                Paragraph with 2rem (32px if root is 16px)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: Percentages</h2>
        <p>
          Percentages are relative to the parent element’s size. Often used in
          width and height.
        </p>

        <CodeCopyBlock
          code={`.box {
  width: 50%;   /* half of parent width */
  height: 100px;
  background: lightblue;
}`}
          language="css"
        />

        <div
          style={{
            border: "1px solid #ddd",
            width: "400px",
            marginTop: "1rem",
          }}
        >
          <div
            style={{
              width: "50%",
              height: "100px",
              background: "lightblue",
              textAlign: "center",
              lineHeight: "100px",
            }}
          >
            50% width
          </div>
        </div>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 4: Viewport Units (vh, vw)</h2>
        <p>
          Viewport units are based on the size of the browser window.{" "}
          <code>1vh</code> = 1% of viewport height, <code>1vw</code> = 1% of
          viewport width.
        </p>

        <CodeCopyBlock
          code={`.full-screen {
  width: 100vw;   /* full viewport width */
  height: 50vh;   /* half viewport height */
  background: lightgreen;
}`}
          language="css"
        />

        <div
          style={{
            width: "100%",
            height: "150px",
            background: "lightgreen",
            textAlign: "center",
            lineHeight: "150px",
            marginTop: "1rem",
          }}
        >
          100vw × 50vh (scaled to viewport)
        </div>
      </div>

      {/* Example 5 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 5: Mixed Units</h2>
        <p>
          Units can be combined to achieve flexible designs. For example, using{" "}
          <code>px</code> for borders and <code>%</code> for width.
        </p>

        <CodeCopyBlock
          code={`.card {
  width: 60%;
  padding: 20px;
  border: 2px solid black;
  font-size: 1.2rem;
}`}
          language="css"
        />

        <div
          style={{
            width: "60%",
            padding: "20px",
            border: "2px solid black",
            fontSize: "1.2rem",
            background: "#f3f4f6",
            marginTop: "1rem",
          }}
        >
          Card with mixed units (%, px, rem)
        </div>
      </div>

      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Practice using <b>relative units</b> like <code>em</code>,{" "}
          <code>rem</code>, and <code>vh/vw</code> to build responsive layouts
          that adapt to different screen sizes.
        </p>
      </div>
    </div>
  );
};

export default CSS_Units;
